import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Plus, Users, FileText, Settings, X, Trash2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'IE', name: 'Ireland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' }
];

const Companies = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    phone: '',
    address: '',
    company_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    country: 'GB'
  });
  const [success, setSuccess] = useState(null);
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isPlatformAdmin = user?.userType === 'PLATFORM_ADMIN';

  const handleViewDetails = (companySlug) => {
    navigate(`/companies/${companySlug}`);
  };

  useEffect(() => {
    // Only fetch when user is loaded
    if (user) {
      fetchCompanies();
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      // Platform admins see all firms, regular users see only their firm
      if (user?.userType === 'PLATFORM_ADMIN') {
        const response = await api.get('/firms');
        setCompanies(response.data.firms || []);
      } else if (user?.company?.slug) {
        // Regular users see only their own firm
        const response = await api.get(`/firms/${user.company.slug}`);
        setCompanies([response.data.firm]);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setError(error.response?.data?.error?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (e, company) => {
    e.stopPropagation();
    setCompanyToDelete(company);
    setShowDeleteModal(true);
    setError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;

    setDeleting(true);
    setError(null);
    try {
      await api.delete(`/firms/${companyToDelete.id}`);
      setSuccess(`Company "${companyToDelete.name}" deleted successfully`);
      setShowDeleteModal(false);
      setCompanyToDelete(null);
      fetchCompanies();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to delete company:', error);
      setError(error.response?.data?.error?.message || 'Failed to delete company');
    } finally {
      setDeleting(false);
    }
  };

  const handleCompanySearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setCompanySearchResults([]);
      setShowCompanyDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/companies-house/search/companies?q=${encodeURIComponent(searchTerm)}`);
      setCompanySearchResults(response.data.items || []);
      setShowCompanyDropdown(true);
    } catch (error) {
      console.error('Company search failed:', error);
      setCompanySearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCompanySelect = async (company) => {
    try {
      const response = await api.get(`/companies-house/company/${company.company_number}`);
      const details = response.data;
      const address = details.registered_office_address || {};

      setFormData(prev => ({
        ...prev,
        name: details.company_name,
        company_number: details.company_number,
        address_line1: address.address_line_1 || '',
        address_line2: address.address_line_2 || '',
        city: address.locality || '',
        postcode: address.postal_code || '',
        country: 'GB'
      }));
      setShowCompanyDropdown(false);
    } catch (error) {
      console.error('Failed to fetch company details:', error);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await api.post('/firms', formData);
      setSuccess('Company created successfully!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        email: '',
        website: '',
        phone: '',
        address: '',
        company_number: '',
        address_line1: '',
        address_line2: '',
        city: '',
        postcode: '',
        country: 'GB'
      });
      fetchCompanies();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to create company:', error);
      setError(error.response?.data?.error?.message || 'Failed to create company');
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isPlatformAdmin ? 'All Companies' : 'My Company'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isPlatformAdmin
            ? 'Manage all companies on the platform'
            : 'View and manage your company settings'}
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Search and Actions */}
      {isPlatformAdmin && (
        <div className="mb-6 flex items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Company
          </button>
        </div>
      )}

      {/* Companies Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading companies...</p>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search' : 'Get started by adding a new company'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company) => (
            <div key={company.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">@{company.slug}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  company.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                  company.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {company.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{company.user_count || 0} users</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <FileText className="w-4 h-4 mr-2" />
                  <span>{company.request_count || 0} requests</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Building2 className="w-4 h-4 mr-2" />
                  <span className="capitalize">{company.billing_plan?.toLowerCase() || 'Free'} Plan</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleViewDetails(company.slug)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View Details
                </button>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleViewDetails(company.slug)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Edit company"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  {isPlatformAdmin && company.slug !== 'acquiva-admin' && (
                    <button
                      onClick={(e) => handleDeleteClick(e, company)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Delete company"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add New Company</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({
                    name: '',
                    email: '',
                    website: '',
                    phone: '',
                    address: '',
                    company_number: '',
                    address_line1: '',
                    address_line2: '',
                    city: '',
                    postcode: '',
                    country: 'GB'
                  });
                  setError(null);
                  setShowCompanyDropdown(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateCompany} className="space-y-4">
              {/* Companies House Search - Full Width */}
              <div className="relative col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Companies House (UK only)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    handleCompanySearch(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Start typing company name..."
                />
                {isSearching && (
                  <div className="absolute right-3 top-9">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  </div>
                )}
                {showCompanyDropdown && companySearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {companySearchResults.map((company) => (
                      <button
                        key={company.company_number}
                        type="button"
                        onClick={() => handleCompanySelect(company)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{company.title}</div>
                        <div className="text-sm text-gray-600">
                          {company.company_number} • {company.address_snippet}
                        </div>
                        {company.company_status && (
                          <div className="text-xs text-gray-500 mt-1">
                            Status: {company.company_status} • Type: {company.company_type}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 2-Column Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Acme Corp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Registration Number
                  </label>
                  <input
                    type="text"
                    value={formData.company_number}
                    onChange={(e) => setFormData({ ...formData, company_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="12345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="contact@acme.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://acme.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+44 20 1234 5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Suite 100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="London"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postcode
                  </label>
                  <input
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="SW1A 1AA"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {COUNTRIES.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      name: '',
                      email: '',
                      website: '',
                      phone: '',
                      address: '',
                      company_number: '',
                      address_line1: '',
                      address_line2: '',
                      city: '',
                      postcode: '',
                      country: 'GB'
                    });
                    setError(null);
                    setShowCompanyDropdown(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && companyToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Delete Company</h2>
              </div>
              <button
                onClick={() => { setShowDeleteModal(false); setCompanyToDelete(null); setError(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{companyToDelete.name}</strong>?
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone. All users and verification requests associated with this company will be permanently removed.
            </p>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setCompanyToDelete(null); setError(null); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;

